import React, { useEffect, useRef, useState } from "react";
import {
  countryListAllApi,
  editStatusUserApi,
  dashboardStatisticsCounterApi,
  userListApi,
  userListExportApi,
} from "../../../axios/services/services";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import ErrorModal from "../Modal/ErrorModal";
import { UserLoadingDialog } from "./user-loading-dialog/UserLoadingDialog";
import { UserReceiptModal } from "./user-receipt-modal/UserReceiptModal";
import { UserCard } from "./UserCard";
import { UserUIProvider } from "./UserUIContext";
import SVG from "react-inlinesvg";
import { isUnauthorized } from "../../utils/utils";
import { initialFilter } from "./UserUIHelpers";
import { UserEditStatusModal } from "./user-edit-status-modal/UserEditStatusModal";
import { useFormik } from "formik";
import { EditStatusUserSchema } from "../../FormikSchema/FormikSchema";
import UpdatedSuccessfulModal from "../Modal/UpdatedSuccessfulModal";

const EditStatusUserInitialValues = {
  name: "",
  status: "",
};

export function UserPage({ history }) {
  const [isLoading, setLoading] = useState(false);
  const [isLoadingEditUserStatus, setLoadingEditUserStatus] = useState(false);
  const [loadingExportButton, setLoadingExportButton] = useState(false);
  const [isConfirmModal, setConfirmModal] = useState(false);
  const [isEditStatusModal, setEditStatusModal] = useState(false);
  const [data, setData] = useState({});
  const [userList, setUserList] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [userListTotalCount, setUserListTotalCount] = useState(0);
  const [countryList, setCountryList] = useState([]);
  const [queryParams, setQueryParamsBase] = useState(initialFilter);
  const [ids, setIds] = useState([]);
  const [isUpdateUserList, setUpdateUserList] = useState(0);
  const [statisticsCounterList, setStatisticsCounterList] = useState(null);
  const [userListExport, setUserListExport] = useState([]);
  const exportCSVButton = useRef();

  useEffect(() => {
    async function getUserList() {
      enableLoading();
      const userListResponse = await userListApi({
        filterValues,
        queryParams,
      });
      disableLoading();
      if (userListResponse.Error) {
        isUnauthorized({ response: userListResponse });
        setErrorMessage(userListResponse.Error.message);
        handleErrorModalShow();
        return;
      }
      setUserListTotalCount(
        userListResponse && userListResponse.count ? userListResponse.count : 0
      );

      setUserList(
        userListResponse && userListResponse.data ? userListResponse.data : []
      );
    }
    getUserList();
  }, [filterValues, isUpdateUserList, queryParams]);

  useEffect(() => {
    async function getCountryList() {
      const countryListResponse = await countryListAllApi({ isActiveOnly: 1 });
      if (countryListResponse.Error) {
        isUnauthorized({ response: countryListResponse });
        setErrorMessage(countryListResponse.Error.message);
        handleErrorModalShow();
        return;
      }

      setCountryList(
        countryListResponse && countryListResponse.data
          ? countryListResponse.data
          : []
      );
    }
    getCountryList();
  }, []);

  useEffect(() => {
    async function getStatisticsCounterList() {
      const statisticsCounterListResponse = await dashboardStatisticsCounterApi();
      if (statisticsCounterListResponse.Error) {
        isUnauthorized({ response: statisticsCounterListResponse });
        setErrorMessage(statisticsCounterListResponse.Error.message);
        handleErrorModalShow();
        return;
      }

      setStatisticsCounterList(
        statisticsCounterListResponse && statisticsCounterListResponse
          ? statisticsCounterListResponse
          : []
      );
    }
    getStatisticsCounterList();
  }, []);

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const enableLoadingEditUserStatus = () => {
    setLoadingEditUserStatus(true);
  };

  const disableLoadingEditUserStatus = () => {
    setLoadingEditUserStatus(false);
  };

  const enableLoadingExportButton = () => {
    setLoadingExportButton(true);
  };

  const disableLoadingExportButton = () => {
    setLoadingExportButton(false);
  };

  const handleErrorModalShow = () => {
    setIsErrorModalOpen(true);
  };

  const handleErrorModalHide = () => {
    setIsErrorModalOpen(false);
  };

  const handleFilterValues = (values) => {
    setFilterValues(values);
  };

  const handleConfirmShow = () => {
    setConfirmModal(true);
  };

  const handleConfirmHide = () => {
    setConfirmModal(false);
  };

  const handleEditStatusShow = () => {
    setEditStatusModal(true);
  };

  const handleEditStatusHide = () => {
    setEditStatusModal(false);
  };

  const handleSetCurrentPage = (page) => {
    setCurrentPage(page);
  };

  const handleSuccessModalShow = () => {
    setIsSuccessModalOpen(true);
  };

  const handleSuccessModalHide = () => {
    setIsSuccessModalOpen(false);
  };

  const userUIEvents = {
    openReceiptDialog: (row) => {
      setData(row);
      handleConfirmShow();
    },
    openEditStatusDialog: (row) => {
      setData(row);
      const { status } = row;
      formikEditStatusUser.setFieldValue("status", status ? "1" : "0");
      handleEditStatusShow();
    },
  };

  const formikEditStatusUser = useFormik({
    initialValues: EditStatusUserInitialValues,
    validationSchema: EditStatusUserSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      editStatusUser(values, setSubmitting);
    },
    onReset: (values, { resetForm }) => {},
  });

  const editStatusUser = async (values, setSubmitting) => {
    const { id } = data;
    const { status } = values;
    let editStatusUserObj = {
      id,
      status: parseInt(status) === 1 ? true : false,
    };
    enableLoadingEditUserStatus();
    const editStatusUserResponse = await editStatusUserApi(editStatusUserObj);
    disableLoadingEditUserStatus();
    setSubmitting(false);
    if (editStatusUserResponse.Error) {
      isUnauthorized({ response: editStatusUserResponse });
      setErrorMessage(editStatusUserResponse.Error.message);
      handleErrorModalShow();
      return;
    }
    setSuccessMessage("User status edited successfully");
    handleSuccessModalShow();
    handleEditStatusHide();
    setUpdateUserList(Math.random());
  };

  const onClickOfEditStatusCancelButton = () => {
    formikEditStatusUser.handleReset();
    handleEditStatusHide();
  };

  const handleExportButton = async () => {
    enableLoadingExportButton();
    const userListExportResponse = await userListExportApi({
      limit: userListTotalCount,
    });
    disableLoadingExportButton();
    if (userListExportResponse.Error) {
      isUnauthorized({ response: userListExportResponse });
      setErrorMessage(userListExportResponse.Error.message);
      handleErrorModalShow();
      return;
    } else {
      setUserListExport(userListExportResponse.data);
      exportCSVButton.current.link.click();
    }
  };

  return (
    <>
      <UserUIProvider
        userUIEvents={userUIEvents}
        queryParams={queryParams}
        setQueryParamsBase={setQueryParamsBase}
        ids={ids}
        setIds={setIds}
      >
        <UserLoadingDialog />

        {isConfirmModal && (
          <UserReceiptModal
            data={data}
            handleConfirmHide={handleConfirmHide}
            isConfirmModal={isConfirmModal}
          />
        )}

        {isEditStatusModal && (
          <UserEditStatusModal
            isLoadingEditUserStatus={isLoadingEditUserStatus}
            isEditStatusModal={isEditStatusModal}
            data={data}
            formikEditStatusUser={formikEditStatusUser}
            onClickOfEditStatusCancelButton={onClickOfEditStatusCancelButton}
          />
        )}

        <UserCard
          isLoading={isLoading}
          userList={userList}
          userListTotalCount={userListTotalCount}
          currentPage={currentPage}
          setCurrentPage={handleSetCurrentPage}
          handleFilterValues={handleFilterValues}
          filterValues={filterValues}
          loadingExportButton={loadingExportButton}
          exportCSVButton={exportCSVButton}
          userListExport={userListExport}
          handleExportButton={handleExportButton}
          countryList={countryList}
          statisticsCounterList={statisticsCounterList}
        />
      </UserUIProvider>

      <UpdatedSuccessfulModal
        message={successMessage}
        UpdatedSuccessfulModalShow={isSuccessModalOpen}
        UpdatedSuccessfulModalHide={handleSuccessModalHide}
        successIcon={
          <div className="success-icon-wrapper text-center">
            <span className="svg-icon menu-icon svg-icon-white svg-icon-4x mr-0">
              <SVG
                src={toAbsoluteUrl("/media/svg/icons/Navigation/Check.svg")}
              />
            </span>
          </div>
        }
      />

      <ErrorModal
        message={errorMessage}
        isErrorModalOpen={isErrorModalOpen}
        hideErrorModal={handleErrorModalHide}
        errorIcon={
          <div className="danger-icon-wrapper text-center">
            <span className="svg-icon menu-icon svg-icon-white svg-icon-4x mr-0">
              <SVG
                src={toAbsoluteUrl("/media/svg/icons/Navigation/Close.svg")}
              />
            </span>
          </div>
        }
        errorButtonLabel={"Close"}
        errorOnClick={handleErrorModalHide}
      />
    </>
  );
}
